import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, makeStyles } from '@material-ui/core';
import axios from 'axios';
import { BASE_API, USER_TOKEN, BASE_S3 } from 'config/consts';
import { useParams } from 'react-router-dom';
import CopaSarauForm from './index';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
  },
  header: {
    backgroundColor: '#10263d',
    color: '#fff',
    padding: '56px 16px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 1,
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(7, 20, 32, 0.32) 0%, rgba(7, 20, 32, 0.62) 100%)',
    zIndex: 1,
  },
  headerContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: 900,
    padding: '0 16px',
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: 0.5,
    color: '#fff',
    textShadow: '0 2px 10px rgba(0,0,0,0.35)',
  },
  headerSubtitle: {
    fontSize: '0.95rem',
    opacity: 1,
    marginTop: 4,
    color: '#fff',
    textShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: 32,
    textAlign: 'center',
  },
}));

export default function CopaSarauStandalone(props) {
  const classes = useStyles();
  const { id: actionId } = useParams();
  const [activity, setActivity] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem(USER_TOKEN));
    if (!token) {
      setError('login');
      setLoading(false);
      return;
    }

    const headers = { Authorization: token };

    // Decode JWT to get user id
    let userId = null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.id;
    } catch (e) {
      console.error('Erro ao decodificar token:', e);
    }

    const activityUrl = userId
      ? `${BASE_API}/actions/${actionId}/?where[user_id]=${userId}&flat=1`
      : `${BASE_API}/public-actions/${actionId}`;

    const userUrl = userId ? `${BASE_API}/users-web/${userId}` : null;

    const promises = [axios.get(activityUrl, { headers })];
    if (userUrl) {
      promises.push(axios.get(userUrl, { headers }).catch(() => null));
    }

    Promise.all(promises)
      .then(([activityRes, userRes]) => {
        if (activityRes.data) {
          if (activityRes.data.type_id !== 43) {
            setError('invalid_activity');
            setLoading(false);
            return;
          }
          setActivity(activityRes.data);
        }
        if (userRes && userRes.data) {
          const userData = Array.isArray(userRes.data) ? userRes.data[0] : userRes.data;
          setUser(userData);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erro ao carregar dados:', err);
        if (err.response && err.response.status === 401) {
          setError('login');
        } else {
          setError('load');
        }
        setLoading(false);
      });
  }, [actionId]);

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>Copa/Sarau Sindilegis 2026</Typography>
            <Typography className={classes.headerSubtitle}>Formulário de Inscrição</Typography>
          </Box>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress style={{ color: '#1a3a5c' }} />
          <Typography style={{ marginTop: 16, color: '#666' }}>Carregando...</Typography>
        </Box>
      </Box>
    );
  }

  if (error === 'login') {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>Copa/Sarau Sindilegis 2026</Typography>
            <Typography className={classes.headerSubtitle}>Formulário de Inscrição</Typography>
          </Box>
        </Box>
        <Box className={classes.errorContainer}>
          <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: 16 }}>
            Sessão expirada
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
            Faça login para acessar o formulário de inscrição.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error === 'invalid_activity') {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>Copa/Sarau Sindilegis 2026</Typography>
            <Typography className={classes.headerSubtitle}>Formulário de Inscrição</Typography>
          </Box>
        </Box>
        <Box className={classes.errorContainer}>
          <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: 16 }}>
            Link inválido
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Esta atividade não corresponde a uma inscrição da Copa/Sarau.
            Verifique o link recebido ou acesse a atividade correta no Sindilegis Mais.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !activity) {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>Copa/Sarau Sindilegis 2026</Typography>
            <Typography className={classes.headerSubtitle}>Formulário de Inscrição</Typography>
          </Box>
        </Box>
        <Box className={classes.errorContainer}>
          <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: 16 }}>
            Erro ao carregar
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Não foi possível carregar os dados da atividade. Tente novamente.
          </Typography>
        </Box>
      </Box>
    );
  }

  const activityName = activity.name || 'Copa/Sarau Sindilegis 2026';
  const bgImage = activity.image ? `${BASE_S3}/${activity.image}` : null;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        {bgImage && <Box className={classes.headerBg} style={{ backgroundImage: `url(${bgImage})` }} />}
        <Box className={classes.headerOverlay} />
        <Box className={classes.headerContent}>
          <Typography className={classes.headerTitle}>{activityName}</Typography>
          <Typography className={classes.headerSubtitle}>Formulário de Inscrição</Typography>
        </Box>
      </Box>
      <CopaSarauForm
        history={props.history}
        activityData={activity}
        userData={user}
        standalone
      />
    </Box>
  );
}
